import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Grid, CircularProgress, Box, Typography, Stack, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import React, { FC, useState, useEffect } from 'react';
import { MdDelete } from 'react-icons/md';
import { ICandidate, ISubmissionFormSettings } from 'wisestep-api';

import {
  formatDateToMonthDayYearString,
  formattedDate,
  getDateUsingFormat,
  timeSince,
} from '../../helper/DateOperations/DateOperations';
import { formatLocation } from '../../helper/LocationUtil/LocationUtil';
import ICandidateAggregatedData from '../../types/ICandidateAggregatedData';
import WsColors from '../../WsColors/index';
import CandidateAccordion from '../CandidateAccordion/CandidateAccordion';
import CandidateCommonContactInfo from '../CandidateCommonContactInfo/CandidateCommonContactInfo';
import CandidateWorkHistory from '../CandidateWorkHistory/CandidateWorkHistory';
import ConfirmationDialog from '../confirmationDialog/ConfirmationDialog';
import Item from '../Item/Item';
import Label from '../Label/Label';
import WsChip, { WsChipVariants } from '../WsChip/WsChip';
import { WsDialog } from '../WsDialog';
import WsTypography, { WsTypoColors, WsTypoVariants } from '../WsTypography/WsTypography';

interface IOtherDocument {
  documentLocation: string;
  originalName: string;
  tag: string | null;
  uploadedOn: Date;
}
interface ICandidateProfileTab {
  ss: 'small' | 'large';
  candidate: ICandidate;
  aggregation?: ICandidateAggregatedData;
  handleProfileAction: (arg1: string, arg2: string | null) => void;
  cvAction?: (args1: string, args2: string | null, args3: string | null) => void;
  deleteCv?: (args1: string, args2: string) => void;
  isAdmin?: boolean;
  otherDocuments?: IOtherDocument[];
  getFieldSettings: (isMapping: boolean) => Promise<ISubmissionFormSettings>;
  view: 'mapping' | 'submission';
}

export const GridItem = styled(Item)(() => ({
  paddingTop: 0,
  paddingBottom: '7px',
}));

const GetLabel: FC<{ title: string }> = ({ title }) => (
  <Grid mt={2} item xs={4}>
    <GridItem>
      <Label>{title}</Label>
    </GridItem>
  </Grid>
);

export const displayValue = (value: string | number | null | undefined, defaultValue: string) => (
  <Box mt={1}>
    <WsTypography bold={true}>{!isEmpty(value) ? value : defaultValue}</WsTypography>
  </Box>
);

export const getSalary = (field: string, candidate: ICandidate): string => {
  if (field == 'current') {
    const latestExp = candidate;
    let salary = '';
    if (
      latestExp?.currentSalaryCurrency &&
      latestExp?.currentSalary &&
      latestExp?.currentSalaryTimeSpan
    ) {
      salary = `${latestExp.currentSalaryCurrency} ${String(latestExp.currentSalary)} 
      ${latestExp.currentSalaryTimeSpan}`;
    }
    return salary;
  } else if (field == 'expected') {
    const latestExp = candidate;
    let salary = '';
    let salaryPerAnnum = '';
    let salaryPerHour = '';
    if (
      latestExp?.expectedBaseSalary &&
      latestExp?.expectedBaseSalaryTimeSpan &&
      latestExp?.expectedSalaryCurrency
    ) {
      salaryPerAnnum = `${latestExp.expectedSalaryCurrency} ${String(latestExp.expectedBaseSalary)} 
      ${latestExp.expectedBaseSalaryTimeSpan}`;
    }
    if (
      latestExp?.workPreferences?.[0]?.hourlyRate?.min &&
      latestExp?.workPreferences?.[0]?.hourlyRate?.currency
    ) {
      salaryPerHour = ` | ${latestExp?.workPreferences?.[0].hourlyRate.currency} ${
        latestExp?.workPreferences?.[0].hourlyRate.min
      } ${'Per Hour'}`;
    }
    salary = salaryPerAnnum + salaryPerHour;
    return isEmpty(salary) ? '' : salary;
  }

  return '';
};

const CandidateProfileTab: FC<ICandidateProfileTab> = ({
  ss,
  candidate,
  aggregation,
  handleProfileAction,
  cvAction,
  deleteCv,
  isAdmin,
  otherDocuments,
  getFieldSettings,
  view,
}) => {
  const candidateAggregatedData = aggregation;
  const [email, setEmail] = useState<string[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);
  const [profileUrls, setProfileUrls] = useState<string[]>([]);
  const [alternateEmail, setAlternateEmail] = useState<string[]>([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [settings, setSettings] = useState<ISubmissionFormSettings | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    (async () => {
      if (settings == null) {
        const res = await getFieldSettings(view === 'submission' ? false : true);
        setSettings(res);
      }
    })();
  }, [getFieldSettings, settings, view]);

  useEffect(() => {
    if (candidateAggregatedData?.candidateEntity?.email) {
      setEmail([candidate.email, ...[candidateAggregatedData.candidateEntity.email]]);
    }
    if (candidateAggregatedData?.candidateEntity?.phoneNumber) {
      if (candidate.phoneNumber) {
        setPhoneNumbers([
          candidate.phoneNumber,
          ...[candidateAggregatedData.candidateEntity.phoneNumber],
          ...candidateAggregatedData.candidateEntity.alternatePhoneNumbers,
        ]);
      } else {
        setPhoneNumbers([
          candidateAggregatedData.candidateEntity.phoneNumber,
          ...candidateAggregatedData.candidateEntity.alternatePhoneNumbers,
        ]);
      }
    }
    if (candidateAggregatedData?.candidateEntity?.profileURLs) {
      setProfileUrls(candidateAggregatedData.candidateEntity.profileURLs);
    }
    if (candidateAggregatedData?.candidateEntity?.alternateEmails) {
      setAlternateEmail(candidateAggregatedData.candidateEntity.alternateEmails);
    }
  }, [candidate.email, candidate.phoneNumber, candidateAggregatedData]);

  const getAllAlternateNumbers = (data: string[]) => {
    if (!isEmpty(data) && data.includes('false')) {
      return data.filter((val) => val != 'false');
    }
    return isEmpty(data) ? [] : data;
  };

  const getExperience = (field: string): string => {
    if (field == 'total') {
      const latestExp = candidate;
      const expInYears = latestExp?.totalYearsExperience
        ? `${latestExp?.totalYearsExperience} Years`
        : '';
      const expInMonths = latestExp?.totalMonthsExperience
        ? `${latestExp?.totalMonthsExperience % 12} Months`
        : '';
      return expInYears || expInMonths ? `${expInYears} ${expInMonths}` : 'Not added';
    } else if (field == 'relevant') {
      const latestExp = candidate;
      const expInYears = latestExp?.relevantYearsExperience
        ? `${latestExp?.relevantYearsExperience} Years`
        : '';
      const expInMonths = latestExp?.relevantMonthsExperience
        ? `${latestExp?.relevantMonthsExperience} Months`
        : '';
      return expInYears || expInMonths ? `${expInYears} ${expInMonths}` : 'Not added';
    }
    return 'Not added';
  };

  return (
    <>
      {!isEmpty(candidateAggregatedData) && settings != null ? (
        <>
          <CandidateAccordion header="Personal Info">
            <Grid container item xs={12}>
              <CandidateCommonContactInfo
                ss={ss}
                email={email}
                tags={candidateAggregatedData?.candidateEntity?.tags ?? []}
                phoneNumbers={getAllAlternateNumbers(phoneNumbers)}
                profileUrls={profileUrls}
                name={candidate.name ?? 'Not added'}
                alternateEmails={alternateEmail}
                settings={settings}
                handleProfileAction={handleProfileAction}
                personalWebsite={candidate?.personalWebsite ?? 'Not added'}
              />
              {((settings?.locations && settings?.locations.hidden === false) ||
                (settings?.currentLocation && settings?.currentLocation.hidden === false)) && (
                <>
                  <GetLabel title="Current Location" />
                  <Grid mt={2} item xs={8}>
                    <GridItem>
                      {displayValue(
                        !isEmpty(candidate.currentLocation)
                          ? formatLocation(candidate.currentLocation)
                          : 'Not added',
                        'Not added'
                      )}
                    </GridItem>
                  </Grid>
                </>
              )}
              {/* {((settings?.candidatePersonalId && settings?.candidatePersonalId.hidden === false) ||
                (settings?.candidateID && settings?.candidateID.hidden === false)) && (
                <>
                  <GetLabel title="Candidate ID" />
                  <Grid mt={2} item xs={8}>
                    <GridItem>
                      {displayValue(
                        candidate.candidateId ?? candidate.candidatePersonalId,
                        'Not added'
                      )}
                    </GridItem>
                  </Grid>
                </>
              )} */}
              {settings?.source && settings?.source.hidden === false && (
                <>
                  <GetLabel title="Source" />
                  <Grid mt={2} item xs={8}>
                    <GridItem>
                      {candidate.sources && candidate?.sources[0]
                        ? displayValue(candidate?.sources[0], 'Not added')
                        : displayValue(candidate?.source, 'Not added')}
                    </GridItem>
                  </Grid>
                </>
              )}
            </Grid>
            {(settings?.workAuthorizationCategory && settings?.workAuthorizationCategory.hidden) ===
              false && (
              <>
                <GetLabel title="Work Authorization Category" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {displayValue(candidate.workAuthorizationCategory, 'Not added')}
                  </GridItem>
                </Grid>
              </>
            )}
            {((settings?.workauthorization && settings?.workauthorization.hidden === false) ||
              (settings?.workAuthorization && settings?.workAuthorization.hidden === false)) && (
              <>
                <GetLabel title="Work Authorization" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate.workAuthorization, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {((settings?.visaStatus && settings?.visaStatus.hidden === false) ||
              (settings?.visaType && settings?.visaType.hidden === false)) && (
              <>
                <GetLabel title="Visa" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate.visaStatus, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {(settings?.visaValidity && settings?.visaValidity.hidden) === false && (
              <>
                <GetLabel title="Visa Validity" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate.visaValidity, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {(settings?.totalExperience && settings?.totalExperience.hidden) === false && (
              <>
                <GetLabel title="Total Yrs of Experience" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    <WsTypography bold={true}>{getExperience('total')}</WsTypography>
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.relevantExperience && settings?.relevantExperience.hidden === false && (
              <>
                <GetLabel title="Relevant Yrs of Experience" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    <WsTypography bold={true}>{getExperience('relevant')}</WsTypography>
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.certifications && settings?.certifications.hidden === false && (
              <>
                <GetLabel title="Certifications" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {isEmpty(candidate?.certifications) ? (
                      <Box mt={1}>
                        <WsTypography bold={true}>{'Not added'}</WsTypography>
                      </Box>
                    ) : (
                      candidate?.certifications?.map((certificate, index: number) => (
                        <WsTypography key={index} bold={true}>
                          {certificate}
                        </WsTypography>
                      ))
                    )}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.languages && settings?.languages.hidden === false && (
              <>
                <GetLabel title="Languages" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {isEmpty(candidate?.candidateLanguages) ? (
                      <Box mt={1}>
                        <WsTypography bold={true}>{'Not added'}</WsTypography>
                      </Box>
                    ) : (
                      candidate?.candidateLanguages?.map((languages, index: number) => (
                        <WsTypography key={index} bold={true}>
                          {languages.languageName}
                        </WsTypography>
                      ))
                    )}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.nationality && settings?.nationality.hidden === false && (
              <>
                <GetLabel title="Nationality" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate.nationality, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {settings?.securityAllowance && settings?.securityAllowance.hidden === false && (
              <>
                <GetLabel title="Security Clearance" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {displayValue(candidate?.securityAllowance?.status ? 'Yes' : 'No', 'Not added')}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.hasVeteranStatus && settings?.hasVeteranStatus.hidden === false && (
              <>
                <GetLabel title="Veteran Status" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {displayValue(candidate?.hasVeteranStatus ? 'Yes' : 'No', 'Not added')}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.passportNumber && settings?.passportNumber.hidden === false && (
              <>
                <GetLabel title="Passport Number" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.passportNumber, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {settings?.passportType && settings?.passportType.hidden === false && (
              <>
                <GetLabel title="Passport Type" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.passportType, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {settings?.dob && settings?.dob.hidden === false && (
              <>
                <GetLabel title="DOB" />

                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {displayValue(
                      candidate?.dob
                        ? formatDateToMonthDayYearString(new Date(candidate?.dob))
                        : 'Not added',
                      'Not added'
                    )}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.age && settings?.age.hidden === false && (
              <>
                <GetLabel title="Age" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.age, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {settings?.gender && settings?.gender.hidden === false && (
              <>
                <GetLabel title="Gender" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.gender, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {settings?.postalAddress && settings?.postalAddress.hidden === false && (
              <>
                <GetLabel title="Postal Address" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.postalAddress, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {settings?.ssnNumber && settings?.ssnNumber.hidden === false && (
              <>
                <GetLabel title="SSN Number" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.ssnNumber, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {settings?.panCard && settings?.panCard.hidden === false && (
              <>
                <GetLabel title="PAN Card" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.panCard, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {((settings?.aadharCard && settings?.aadharCard.hidden === false) ||
              (settings?.aadharNumber && settings?.aadharNumber.hidden === false)) && (
              <>
                <GetLabel title="Aadhar Number" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.aadharNumber, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
          </CandidateAccordion>
          <CandidateAccordion header="Work Experience">
            {settings?.skills && settings?.skills.hidden === false && (
              <>
                <GetLabel title="Skills" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {!isEmpty(candidate.skills) ? (
                      <>
                        {showAll ? (
                          <>
                            <Grid
                              container
                              direction="row"
                              spacing={3}
                              sx={{ maxWidth: '100%', overflowX: 'auto' }}
                            >
                              {candidate?.skills?.map((skill, index: number) => (
                                <Box key={index} marginBottom="2px" paddingTop="15px">
                                  <WsChip value={skill} variant={WsChipVariants.PRIMARY} />
                                </Box>
                              ))}
                              <Box sx={{ float: 'right' }} mt={2.5}>
                                <WsTypography
                                  onClick={() => setShowAll(false)}
                                  cursor="pointer"
                                  color={WsTypoColors.PRIMARY}
                                >
                                  show less
                                </WsTypography>
                              </Box>
                            </Grid>
                          </>
                        ) : (
                          <>
                            <Grid
                              container
                              direction="row"
                              spacing={3}
                              sx={{ maxWidth: '100%', overflowX: 'auto' }}
                            >
                              {candidate?.skills?.slice(0, 5).map((skill, index: number) => (
                                <Box key={index} marginBottom="2px" paddingTop="15px">
                                  <WsChip value={skill} variant={WsChipVariants.PRIMARY} />
                                </Box>
                              ))}
                              {candidate?.skills?.length > 5 && (
                                <Box sx={{ float: 'right' }} mt={2.5}>
                                  <WsTypography
                                    onClick={() => setShowAll(true)}
                                    cursor="pointer"
                                    color={WsTypoColors.PRIMARY}
                                  >
                                    {'+' + (candidate?.skills?.length - 5) + 'more'}
                                  </WsTypography>
                                </Box>
                              )}
                            </Grid>
                          </>
                        )}
                      </>
                    ) : (
                      <WsTypography bold={true}>{'Not added'}</WsTypography>
                    )}
                  </GridItem>
                </Grid>
              </>
            )}
            {((settings?.jobTitle && settings?.jobTitle.hidden === false) ||
              (settings?.currentJobTitle && settings?.currentJobTitle.hidden === false)) && (
              <>
                <GetLabel title="Latest Job Title" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {displayValue(
                      candidate.currentJobTitle ?? candidate?.previousJobTitles?.[0],
                      'Not added'
                    )}
                  </GridItem>
                </Grid>
              </>
            )}
            {((settings?.currentCompany && settings?.currentCompany.hidden === false) ||
              (settings?.currentEmployer && settings?.currentEmployer.hidden === false)) && (
              <>
                <GetLabel title="Current Job Employer" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {displayValue(candidate.currentEmployer ?? candidate.companyName, 'Not added')}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.previousJobTitles && settings?.previousJobTitles.hidden === false && (
              <>
                {isEmpty(candidate.experience?.history) && isEmpty(candidate.previousJobTitles) ? (
                  <>
                    <GetLabel title="Previous Job Title" />
                    <Grid mt={2} item xs={8}>
                      <GridItem>
                        <WsTypography bold={true}>{'Not added'}</WsTypography>
                      </GridItem>
                    </Grid>
                  </>
                ) : (
                  <>
                    <GetLabel title="Previous Job Title" />
                    <Grid mt={2} item xs={8}>
                      <GridItem>
                        <CandidateWorkHistory candidate={candidate} />
                      </GridItem>
                    </Grid>
                  </>
                )}
              </>
            )}
            {((settings?.prevEmployers && settings?.prevEmployers.hidden === false) ||
              (settings?.previousEmployers && settings?.previousEmployers.hidden === false)) && (
              <>
                <GetLabel title="Previous Employers" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {!isEmpty(candidate.previousEmployers) ? (
                      <>
                        {candidate.previousEmployers.map((title, index) => (
                          <WsTypography key={index} bold={true} display="inline">
                            {`${index > 0 ? `, ${title}` : title}`}
                          </WsTypography>
                        ))}
                      </>
                    ) : (
                      <WsTypography bold={true}>{'Not added'}</WsTypography>
                    )}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.highestEducation && settings?.highestEducation.hidden === false && (
              <>
                <GetLabel title="Highest Education Qualifications" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {isEmpty(candidate?.educationQualifications) ? (
                      <Box mt={1}>
                        <WsTypography bold={true}>{'Not added'}</WsTypography>
                      </Box>
                    ) : (
                      candidate?.educationQualifications?.map((education, index: number) => (
                        <WsTypography key={index} bold={true}>
                          {education}
                        </WsTypography>
                      ))
                    )}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.institutesAttended && settings?.institutesAttended.hidden === false && (
              <>
                <GetLabel title="Institutions Attended" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {!isEmpty(candidate?.institutionsAttended) ? (
                      <>
                        {candidate?.institutionsAttended?.map((value) =>
                          displayValue(value, 'Not added')
                        )}
                      </>
                    ) : (
                      <WsTypography bold={true}>{'Not added'}</WsTypography>
                    )}
                  </GridItem>
                </Grid>
              </>
            )}
          </CandidateAccordion>
          <CandidateAccordion header="Job Preferences">
            {((settings?.jobIncludesTravel && settings?.jobIncludesTravel.hidden === false) ||
              (settings?.currentJobIncludeTravel &&
                settings?.currentJobIncludeTravel.hidden === false)) && (
              <>
                <GetLabel title="Current Job Includes Travel" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {displayValue(candidate?.jobIncludesTravel ? 'Yes' : 'No', 'Not added')}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.willingToTravel && settings?.willingToTravel.hidden === false && (
              <>
                <GetLabel title="Willing to Travel" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {displayValue(candidate?.willingToTravel ? 'Yes' : 'No', 'Not added')}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.travelPreference && settings?.travelPreference.hidden === false && (
              <>
                <GetLabel title="Travel Preferences" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.willingToTravelType, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {settings?.willingToTravel && settings?.willingToTravel.hidden === false && (
              <>
                <GetLabel title="Willing to Relocate" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {displayValue(candidate?.willingToRelocate ? 'Yes' : 'No', 'Not added')}
                  </GridItem>
                </Grid>
              </>
            )}
            {((settings?.relocationPreference && settings?.relocationPreference.hidden === false) ||
              (settings?.relocationPreferences &&
                settings?.relocationPreferences.hidden === false)) && (
              <>
                <GetLabel title="Relocation Preferences" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.relocationPreference, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {settings?.familyWillingToRelocate &&
              settings?.familyWillingToRelocate.hidden === false && (
                <>
                  <GetLabel title="Family Willing to Relocate" />
                  <Grid mt={2} item xs={8}>
                    <GridItem>
                      <WsTypography bold={true}>
                        {candidate?.familyWillingToRelocate == true ? 'Yes' : 'No'}
                      </WsTypography>
                    </GridItem>
                  </Grid>
                </>
              )}
            {((settings?.noticePeriodInDays && settings?.noticePeriodInDays.hidden === false) ||
              (settings?.noticePeriod && settings?.noticePeriod.hidden === false)) && (
              <>
                <GetLabel title="Notice Period" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.noticePeriodInDays, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {((settings?.communicationPreference &&
              settings?.communicationPreference.hidden === false) ||
              (settings?.communicationPreferences &&
                settings?.communicationPreferences.hidden === false)) && (
              <>
                <GetLabel title="Communication Preference" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    <Box mt={1}>
                      {candidate?.commPreferencePhone && (
                        <WsTypography bold={true}>Phone</WsTypography>
                      )}
                    </Box>
                    <Box mt={1}>
                      {candidate?.commPreferenceEmail && (
                        <WsTypography bold={true}>Email</WsTypography>
                      )}
                    </Box>
                  </GridItem>
                </Grid>
              </>
            )}
            {((settings?.jobPreference && settings?.jobPreference.hidden === false) ||
              (settings?.jobPreferences && settings?.jobPreferences.hidden === false)) && (
              <>
                <GetLabel title="Job Preference" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {candidate?.jobPreferenceContract || candidate?.jobPreferencePermanent ? (
                      <>
                        <Box mt={1}>
                          {candidate?.jobPreferenceContract && (
                            <WsTypography bold={true}>Contract</WsTypography>
                          )}
                        </Box>
                        <Box mt={1}>
                          {candidate?.jobPreferencePermanent && (
                            <WsTypography bold={true}>Permanent</WsTypography>
                          )}
                        </Box>
                      </>
                    ) : (
                      <>
                        {!isEmpty(candidate?.preferredJobTypes) ? (
                          <>
                            {candidate?.preferredJobTypes?.map((jobType, index: number) => (
                              <WsTypography key={index} bold={true}>
                                {jobType}
                              </WsTypography>
                            ))}
                          </>
                        ) : (
                          <WsTypography bold={true}>{'Not added'}</WsTypography>
                        )}
                      </>
                    )}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.availableFrom && settings?.availableFrom.hidden === false && (
              <>
                <GetLabel title="Available From" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {candidate.availableFrom ? (
                      <Box mt={1}>
                        <WsTypography bold={true}>
                          {formattedDate(candidate.availableFrom)}
                        </WsTypography>
                      </Box>
                    ) : (
                      <Box mt={1}>
                        <WsTypography bold={true}>Not added</WsTypography>
                      </Box>
                    )}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.desiredJobTitles && settings?.desiredJobTitles.hidden === false && (
              <>
                {!isEmpty(candidate.desiredJobTitles) && (
                  <>
                    <GetLabel title="Prefered Job Title" />
                    <Grid mt={2} item xs={8}>
                      <GridItem>
                        {!candidate?.desiredJobTitles ? (
                          <Box mt={1}>
                            <WsTypography bold={true}>{'Not added'}</WsTypography>
                          </Box>
                        ) : (
                          candidate?.desiredJobTitles?.map((job, index: number) => (
                            <WsTypography key={index} bold={true}>
                              {job}
                            </WsTypography>
                          ))
                        )}
                      </GridItem>
                    </Grid>
                  </>
                )}
              </>
            )}

            {settings?.preferredLocation && settings?.preferredLocation.hidden === false && (
              <>
                <GetLabel title="Prefered Location" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {isEmpty(candidate?.preferredLocations) ? (
                      <Box mt={1}>
                        <WsTypography bold={true}>{'Not added'}</WsTypography>
                      </Box>
                    ) : (
                      candidate?.preferredLocations?.map((location, index: number) => (
                        <WsTypography key={index} bold={true}>
                          {formatLocation(location)}
                        </WsTypography>
                      ))
                    )}
                  </GridItem>
                </Grid>
              </>
            )}
            {((settings?.jobType && settings?.jobType.hidden === false) ||
              (settings?.currentJobType && settings?.currentJobType.hidden === false)) && (
              <>
                <GetLabel title="Current Job Type" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.currentJobType, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {((settings?.currentCompensation && settings?.currentCompensation.hidden === false) ||
              (settings?.currentSalary && settings?.currentSalary.hidden === false)) && (
              <>
                <GetLabel title="Current Salary" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    <WsTypography bold={true}>{getSalary('current', candidate)}</WsTypography>
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.otherAllowance && settings?.otherAllowance.hidden === false && (
              <>
                <GetLabel title="Allowances & Benefits" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {displayValue(candidate?.otherCurrentAllowances, 'Not added')}
                  </GridItem>
                </Grid>
              </>
            )}
            {((settings?.expectedCompensation && settings?.expectedCompensation.hidden === false) ||
              (settings?.expectedSalary && settings?.expectedSalary.hidden === false)) && (
              <>
                <GetLabel title="Expected Salary" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    <WsTypography bold={true}>{getSalary('expected', candidate)}</WsTypography>
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.expectedAllowance && settings?.expectedAllowance.hidden === false && (
              <>
                <GetLabel title="Expected Allowances & Benefits" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {displayValue(candidate?.otherExpectedAllowances, 'Not added')}
                  </GridItem>
                </Grid>
              </>
            )}
            {candidate?.likelyToMove == true && <GetLabel title="Likely to Switch" />}
            {candidate?.likelyToMove == true && (
              <Grid mt={2} item xs={8}>
                <GridItem>
                  {displayValue(candidate?.likelyToMove == true ? 'Yes' : 'No', 'Not added')}
                </GridItem>
              </Grid>
            )}
            {candidate?.likelyToMove == true && <GetLabel title="Likely to Move score" />}
            {candidate?.likelyToMove == true && (
              <Grid mt={2} item xs={8}>
                <GridItem>
                  {displayValue(String(candidate?.likelyToMoveScore), 'Not added')}
                </GridItem>
              </Grid>
            )}
          </CandidateAccordion>
          <CandidateAccordion header="Assessment">
            {((settings?.interviewingWithOthers &&
              settings?.interviewingWithOthers.hidden === false) ||
              (settings?.interviewingAnywhereElse &&
                settings?.interviewingAnywhereElse.hidden === false)) && (
              <>
                <GetLabel title="Interviewing with others" />
                <Grid mt={2} item xs={8}>
                  <GridItem>
                    {displayValue(candidate?.interviewingWithOthers ? 'Yes' : 'No', 'Not added')}
                  </GridItem>
                </Grid>
              </>
            )}
            {settings?.otherInterviewStage && settings.otherInterviewStage.hidden === false && (
              <>
                <GetLabel title="Other Interview Stage" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.otherInterviewStage, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {((settings?.jobChangeReason && settings?.jobChangeReason.hidden === false) ||
              (settings?.reasonForChange && settings?.reasonForChange.hidden === false)) && (
              <>
                <GetLabel title="Reason looking for a new job" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.reasonForNewJob, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            {((settings?.recruiterAssessment && settings?.recruiterAssessment.hidden === false) ||
              (settings?.yourAssessment && settings?.yourAssessment.hidden === false)) && (
              <>
                <GetLabel title="Recruiters assessment" />
                <Grid mt={2} item xs={8}>
                  <GridItem>{displayValue(candidate?.assessment, 'Not added')}</GridItem>
                </Grid>
              </>
            )}
            <GetLabel title="I have spoken to the Candidate for their interest in this Position and have obtained the Right to Represent" />
            <Grid mt={2} item xs={8}>
              <GridItem>
                <WsTypography bold={true}>{candidate?.rtrConsent ? 'Yes' : 'N/A'}</WsTypography>
              </GridItem>
            </Grid>
          </CandidateAccordion>
          {(candidate?.bio ?? candidate?.candidateSummary) && (
            <CandidateAccordion header="Candidate Summary">
              <GetLabel title="Summary" />
              <Grid mt={2} item xs={8}>
                <GridItem>
                  <WsTypography bold={true}>
                    {candidate?.bio ?? candidate?.candidateSummary}
                  </WsTypography>
                </GridItem>
              </Grid>
            </CandidateAccordion>
          )}
          {view === 'mapping' && settings?.uploadCV && settings?.uploadCV.hidden === false && (
            <CandidateAccordion header="Resume Documents">
              {candidate?.allCandidateResumes?.map((resume, index: number) => (
                <Grid container key={index} direction="row" spacing={2} mb={2}>
                  <Grid pl={2} xs={5}>
                    <GridItem>
                      <Typography
                        color={WsColors.GenericRichBlack}
                        noWrap
                        variant={WsTypoVariants.BODY1}
                      >
                        {resume?.resumeFileOriginalName ?? ''}
                      </Typography>
                    </GridItem>
                  </Grid>
                  <Grid xs={2}>
                    <GridItem>
                      {resume?.resumeUploadedOn ? (
                        <WsTypography
                          cursor="pointer"
                          hoverText={
                            resume?.resumeUploadedOn
                              ? getDateUsingFormat(
                                  String(resume?.resumeUploadedOn),
                                  'D MMMM YYYY, HH:mm'
                                )
                              : ''
                          }
                        >
                          {timeSince(String(resume?.resumeUploadedOn))}
                        </WsTypography>
                      ) : (
                        <WsTypography bold={true}>{'Not added'}</WsTypography>
                      )}
                    </GridItem>
                  </Grid>
                  <Grid xs={4}>
                    <Stack direction="row" spacing={2}>
                      <DownloadIcon
                        onClick={() =>
                          cvAction &&
                          cvAction(
                            'download',
                            resume.resumeLocation ?? '',
                            resume.resumeFileOriginalName
                          )
                        }
                        sx={{
                          color: WsColors.TheBlues500,
                          fontSize: '24px',
                          cursor: 'pointer',
                        }}
                      />
                      <VisibilityIcon
                        onClick={() => {
                          cvAction &&
                            cvAction(
                              'view',
                              resume.resumeLocation ?? '',
                              resume?.resumeFileOriginalName ?? ''
                            );
                        }}
                        sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                      />
                      {isAdmin && (
                        <IconButton
                          onClick={() => setOpenDelete(true)}
                          sx={{
                            p: 0,
                            pb: 0.5,
                            '&:hover': {
                              background: 'none',
                            },
                          }}
                        >
                          <MdDelete color={WsColors.TheBlues500} size={20} />
                        </IconButton>
                      )}
                    </Stack>
                    <WsDialog openDialog={openDelete} onClose={() => setOpenDelete(false)}>
                      <ConfirmationDialog
                        text="Are you sure want to delete CV"
                        title="Delete CV"
                        confirm={() => {
                          deleteCv && deleteCv(candidate.id ?? '', resume.s3FileName ?? '');
                          setOpenDelete(false);
                        }}
                        close={() => setOpenDelete(false)}
                      />
                    </WsDialog>
                  </Grid>
                </Grid>
              ))}
            </CandidateAccordion>
          )}

          {view === 'submission' && settings?.resume && settings?.resume.hidden === false && (
            <CandidateAccordion header="Resume Documents">
              <Grid container direction="row" spacing={2} mb={2}>
                <Grid pl={2} xs={5}>
                  <GridItem>
                    <Typography
                      color={WsColors.GenericRichBlack}
                      noWrap
                      variant={WsTypoVariants.BODY1}
                    >
                      {candidate?.resumeFileOriginalName ?? ''}
                    </Typography>
                  </GridItem>
                </Grid>
                <Grid xs={2}>
                  <GridItem>
                    {candidate?.resumeUploadedOn ? (
                      <WsTypography
                        cursor="pointer"
                        hoverText={
                          candidate?.resumeUploadedOn
                            ? getDateUsingFormat(
                                String(candidate?.resumeUploadedOn),
                                'D MMMM YYYY, HH:mm'
                              )
                            : ''
                        }
                      >
                        {timeSince(String(candidate?.resumeUploadedOn))}
                      </WsTypography>
                    ) : (
                      <WsTypography bold={true}>{'Not added'}</WsTypography>
                    )}
                  </GridItem>
                </Grid>
                <Grid xs={4}>
                  <Stack direction="row" spacing={2}>
                    <DownloadIcon
                      onClick={() =>
                        cvAction &&
                        cvAction(
                          'download',
                          candidate.resumeLocation ?? '',
                          candidate.resumeFileOriginalName
                        )
                      }
                      sx={{
                        color: WsColors.TheBlues500,
                        fontSize: '24px',
                        cursor: 'pointer',
                      }}
                    />
                    <VisibilityIcon
                      onClick={() => {
                        cvAction &&
                          cvAction(
                            'view',
                            candidate.resumeLocation ?? '',
                            candidate?.resumeFileOriginalName ?? ''
                          );
                      }}
                      sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                    />
                    {isAdmin && (
                      <IconButton
                        onClick={() => setOpenDelete(true)}
                        sx={{
                          p: 0,
                          pb: 0.5,
                          '&:hover': {
                            background: 'none',
                          },
                        }}
                      >
                        <MdDelete color={WsColors.TheBlues500} size={20} />
                      </IconButton>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </CandidateAccordion>
          )}
          {settings?.otherDocuments && settings?.otherDocuments.hidden === false && (
            <CandidateAccordion header="Other Documents">
              {otherDocuments?.map((otherDocument, index: number) => (
                <Grid container key={index} direction="row" spacing={2} mb={2}>
                  <Grid pl={2} xs={5}>
                    <GridItem>
                      <Typography
                        color={WsColors.GenericRichBlack}
                        noWrap
                        variant={WsTypoVariants.BODY1}
                      >
                        {otherDocument?.originalName ?? ''}
                      </Typography>
                    </GridItem>
                  </Grid>
                  <Grid xs={2}>
                    <GridItem>
                      {otherDocument?.uploadedOn ? (
                        <WsTypography
                          cursor="pointer"
                          hoverText={
                            otherDocument?.uploadedOn
                              ? getDateUsingFormat(
                                  String(otherDocument?.uploadedOn),
                                  'D MMMM YYYY, HH:mm'
                                )
                              : ''
                          }
                        >
                          {timeSince(String(otherDocument?.uploadedOn))}
                        </WsTypography>
                      ) : (
                        <WsTypography bold={true}>{'Not added'}</WsTypography>
                      )}
                    </GridItem>
                  </Grid>
                  <Grid xs={4}>
                    <Stack direction="row" spacing={2}>
                      <DownloadIcon
                        onClick={() =>
                          cvAction &&
                          cvAction(
                            'download',
                            otherDocument.documentLocation ?? '',
                            otherDocument.originalName
                          )
                        }
                        sx={{
                          color: WsColors.TheBlues500,
                          fontSize: '24px',
                          cursor: 'pointer',
                        }}
                      />
                      <VisibilityIcon
                        onClick={() => {
                          cvAction &&
                            cvAction(
                              'view',
                              otherDocument.documentLocation ?? '',
                              otherDocument?.originalName ?? ''
                            );
                        }}
                        sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              ))}
            </CandidateAccordion>
          )}
        </>
      ) : (
        <Box textAlign="center">
          <CircularProgress disableShrink sx={{ color: WsColors.TheBlues500 }} />
        </Box>
      )}
    </>
  );
};

export default CandidateProfileTab;
